import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { templateService, ShopTypeTemplate } from '../../services/configurationService';
import { customToast } from '../../utils/customToast';
import { Check, AlertCircle, Eye } from 'lucide-react';
import { getShopIcon } from '../../utils/iconMapper';
import TemplatePreviewModal from './TemplatePreviewModal';

const TemplatesManagement: React.FC = () => {
  const { theme } = useTheme();
  const [templates, setTemplates] = useState<ShopTypeTemplate[]>([]);
  const [currentTemplateId, setCurrentTemplateId] = useState<number | null>(null);
  const [previewTemplateId, setPreviewTemplateId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateService.getAll();
      setTemplates(response.templates);
      setCurrentTemplateId(response.current_template_id);
    } catch (error: any) {
      customToast.error('Failed to load templates');
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTemplate = (templateId: number) => {
    setPreviewTemplateId(templateId);
  };

  const handleApplyTemplate = async (replaceExisting: boolean) => {
    if (!previewTemplateId) return;

    try {
      const response = await templateService.applyTemplate(previewTemplateId, replaceExisting);
      customToast.success(
        `Template applied successfully! Added ${response.categories_count} categories and ${response.units_count} units.`
      );
      setPreviewTemplateId(null);
      fetchTemplates(); // Reload to update current template
    } catch (error: any) {
      customToast.error(error.response?.data?.message || 'Failed to apply template');
      console.error('Error applying template:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className={`rounded-lg p-4 ${
        theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
      }`}>
        <div className="flex">
          <AlertCircle className={`h-5 w-5 mt-0.5 ${
            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
          }`} />
          <div className="ml-3">
            <h3 className={`text-sm font-medium ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-800'
            }`}>
              Choose Your Shop Type
            </h3>
            <div className={`mt-2 text-sm ${
              theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
            }`}>
              <p>
                Select a template that matches your business type. Each template comes with pre-configured
                categories, unit types, and custom fields tailored for that industry.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Templates Grid or Skeleton */}
      {loading ? (() => {
        const skeletonColor = theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300';
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((idx) => (
              <div
                key={idx}
                className={`rounded-lg border p-4 ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}
              >
                {/* Header: Icon + Name + Badges */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2 flex-1">
                    {/* Icon placeholder */}
                    <div className={`p-1.5 rounded-lg flex-shrink-0 w-8 h-8 animate-pulse ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                    }`} />
                    <div className="flex-1">
                      {/* Template name skeleton */}
                      <div className={`h-4 w-28 rounded animate-pulse mb-1.5 ${skeletonColor}`} />
                      {/* Badges row - Default + Active */}
                      <div className="flex gap-1.5">
                        <div className={`h-5 w-14 rounded animate-pulse ${skeletonColor}`} />
                        <div className={`h-5 w-16 rounded animate-pulse ${skeletonColor}`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mt-3 space-y-1">
                  <div className={`h-3 w-full rounded animate-pulse ${skeletonColor}`} />
                  <div className={`h-3 w-5/6 rounded animate-pulse ${skeletonColor}`} />
                </div>

                {/* Stats: Categories + Units */}
                <div className="mt-3 flex items-center gap-3 text-xs">
                  <div className={`h-4 w-24 rounded animate-pulse ${skeletonColor}`} />
                  <div className={`h-4 w-20 rounded animate-pulse ${skeletonColor}`} />
                </div>

                {/* Preview & Apply Button */}
                <div className={`mt-3 w-full h-9 rounded-lg animate-pulse ${skeletonColor}`} />
              </div>
            ))}
          </div>
        );
      })() : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => {
            const isCurrentTemplate = template.id === currentTemplateId;
            
            return (
              <div
                key={template.id}
                className={`rounded-lg border p-4 transition-all ${
                  isCurrentTemplate
                    ? theme === 'dark'
                      ? 'bg-blue-900/20 border-blue-600 ring-2 ring-blue-600'
                      : 'bg-blue-50 border-blue-400 ring-2 ring-blue-400'
                    : theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 hover:border-blue-500'
                    : 'bg-white border-gray-200 hover:border-blue-400'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2 flex-1">
                    <div className={`p-1.5 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                        {getShopIcon(template.icon, 20)}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-1.5 flex-wrap">
                        <h3 className={`font-semibold text-sm ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {template.name}
                        </h3>
                        {template.is_default && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            Default
                          </span>
                        )}
                        {isCurrentTemplate && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            <Check size={10} className="mr-1" />
                            Active
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {template.description && (
                  <p className={`mt-2 text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {template.description}
                  </p>
                )}

                <div className="mt-3 flex items-center gap-3 text-xs">
                  {template.default_categories && template.default_categories.length > 0 && (
                    <div>
                      <span className={`font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Categories: 
                      </span>
                      <span className={`ml-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {template.default_categories.length}
                      </span>
                    </div>
                  )}
                  
                  {template.default_units && template.default_units.length > 0 && (
                    <div>
                      <span className={`font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Units: 
                      </span>
                      <span className={`ml-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {template.default_units.length}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handlePreviewTemplate(template.id)}
                  disabled={isCurrentTemplate}
                  className={`mt-3 w-full px-3 py-1.5 ${
                    isCurrentTemplate 
                      ? 'bg-blue-700 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white rounded-lg transition-colors flex items-center justify-center space-x-2 text-sm`}
                >
                  {isCurrentTemplate ? (
                    <>
                      <Check className="h-3.5 w-3.5" />
                      <span>Activated</span>
                    </>
                  ) : (
                    <>
                      <Eye className="h-3.5 w-3.5" />
                      <span>Preview & Apply</span>
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplateId && (
        <TemplatePreviewModal
          templateId={previewTemplateId}
          onClose={() => setPreviewTemplateId(null)}
          onApply={handleApplyTemplate}
        />
      )}
    </div>
  );
};

export default TemplatesManagement;
